/**
 * Voice Allocator for S-DSP
 *
 * Manages allocation of 8 hardware voices to notes.
 * Handles voice stealing when polyphony is exceeded.
 */

/**
 * State of a single voice
 */
export type VoiceState = 'free' | 'playing' | 'releasing';

/**
 * Information about an allocated voice
 */
export interface VoiceInfo {
  readonly voiceIndex: number;
  readonly instrumentId: string;
  readonly pitch: number;
  readonly noteId: string; // Unique identifier for the note instance
  readonly startTime: number; // AudioContext time when note started
  readonly state: VoiceState;
}

/**
 * Result of a voice allocation attempt
 */
export interface AllocationResult {
  readonly voiceIndex: number;
  readonly stolen: boolean; // true if we stole from another note
  readonly stolenNoteId?: string; // ID of note that was stolen from
}

/**
 * Voice allocator for 8-voice S-DSP polyphony.
 *
 * Allocation priority:
 * 1. Same instrument + same pitch (retrigger)
 * 2. Free voice
 * 3. Releasing voice (same instrument preferred)
 * 4. Oldest playing voice (voice stealing)
 */
export class VoiceAllocator {
  private readonly voiceCount: number;
  private voices: (VoiceInfo | null)[];

  constructor(voiceCount: number = 8) {
    this.voiceCount = voiceCount;
    this.voices = new Array(voiceCount).fill(null);
  }

  /**
   * Allocate a voice for a note.
   * Returns the voice index and whether stealing occurred.
   */
  allocate(
    instrumentId: string,
    pitch: number,
    noteId: string,
    currentTime: number
  ): AllocationResult {
    // Priority 1: Same instrument + same pitch (retrigger)
    const retriggerVoice = this.findRetriggerVoice(instrumentId, pitch);
    if (retriggerVoice !== -1) {
      const stolen = this.voices[retriggerVoice];
      this.voices[retriggerVoice] = {
        voiceIndex: retriggerVoice,
        instrumentId,
        pitch,
        noteId,
        startTime: currentTime,
        state: 'playing',
      };
      return {
        voiceIndex: retriggerVoice,
        stolen: true,
        stolenNoteId: stolen?.noteId,
      };
    }

    // Priority 2: Free voice
    const freeVoice = this.findFreeVoice();
    if (freeVoice !== -1) {
      this.voices[freeVoice] = {
        voiceIndex: freeVoice,
        instrumentId,
        pitch,
        noteId,
        startTime: currentTime,
        state: 'playing',
      };
      return { voiceIndex: freeVoice, stolen: false };
    }

    // Priority 3: Releasing voice (prefer same instrument)
    const releasingVoice = this.findReleasingVoice(instrumentId);
    if (releasingVoice !== -1) {
      const stolen = this.voices[releasingVoice];
      this.voices[releasingVoice] = {
        voiceIndex: releasingVoice,
        instrumentId,
        pitch,
        noteId,
        startTime: currentTime,
        state: 'playing',
      };
      return {
        voiceIndex: releasingVoice,
        stolen: true,
        stolenNoteId: stolen?.noteId,
      };
    }

    // Priority 4: Steal oldest playing voice
    const oldestVoice = this.findOldestVoice();
    const stolen = this.voices[oldestVoice];
    this.voices[oldestVoice] = {
      voiceIndex: oldestVoice,
      instrumentId,
      pitch,
      noteId,
      startTime: currentTime,
      state: 'playing',
    };
    return {
      voiceIndex: oldestVoice,
      stolen: true,
      stolenNoteId: stolen?.noteId,
    };
  }

  /**
   * Release a voice (transition to releasing state).
   * Call this on NoteOff - voice enters release phase of envelope.
   */
  release(noteId: string): boolean {
    const voiceIndex = this.findVoiceByNoteId(noteId);
    if (voiceIndex === -1) return false;

    const voice = this.voices[voiceIndex];
    if (voice && voice.state === 'playing') {
      this.voices[voiceIndex] = { ...voice, state: 'releasing' };
      return true;
    }
    return false;
  }

  /**
   * Free a voice completely (after release phase completes).
   */
  free(voiceIndex: number): void {
    if (voiceIndex >= 0 && voiceIndex < this.voiceCount) {
      this.voices[voiceIndex] = null;
    }
  }

  /**
   * Free a voice by note ID.
   */
  freeByNoteId(noteId: string): boolean {
    const voiceIndex = this.findVoiceByNoteId(noteId);
    if (voiceIndex === -1) return false;
    this.voices[voiceIndex] = null;
    return true;
  }

  /**
   * Get current state of a voice.
   */
  getVoice(voiceIndex: number): VoiceInfo | null {
    return this.voices[voiceIndex] ?? null;
  }

  /**
   * Get all active voices.
   */
  getActiveVoices(): VoiceInfo[] {
    return this.voices.filter((v): v is VoiceInfo => v !== null);
  }

  /**
   * Find voice by note ID.
   */
  findVoiceByNoteId(noteId: string): number {
    return this.voices.findIndex((v) => v?.noteId === noteId);
  }

  /**
   * Get count of voices in each state.
   */
  getVoiceCounts(): { free: number; playing: number; releasing: number } {
    let free = 0;
    let playing = 0;
    let releasing = 0;

    for (const voice of this.voices) {
      if (voice === null) {
        free++;
      } else if (voice.state === 'playing') {
        playing++;
      } else if (voice.state === 'releasing') {
        releasing++;
      }
    }

    return { free, playing, releasing };
  }

  /**
   * Reset all voices to free.
   */
  reset(): void {
    this.voices = new Array(this.voiceCount).fill(null);
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private findRetriggerVoice(instrumentId: string, pitch: number): number {
    return this.voices.findIndex(
      (v) => v !== null && v.instrumentId === instrumentId && v.pitch === pitch
    );
  }

  private findFreeVoice(): number {
    return this.voices.findIndex((v) => v === null);
  }

  private findReleasingVoice(preferInstrumentId: string): number {
    // First try same instrument
    const sameInstrument = this.voices.findIndex(
      (v) => v !== null && v.state === 'releasing' && v.instrumentId === preferInstrumentId
    );
    if (sameInstrument !== -1) return sameInstrument;

    // Then any releasing voice
    return this.voices.findIndex((v) => v !== null && v.state === 'releasing');
  }

  private findOldestVoice(): number {
    let oldestIndex = 0;
    let oldestTime = Infinity;

    for (let i = 0; i < this.voices.length; i++) {
      const voice = this.voices[i];
      if (voice !== null && voice.startTime < oldestTime) {
        oldestTime = voice.startTime;
        oldestIndex = i;
      }
    }

    return oldestIndex;
  }
}
